import { Button } from 'devextreme-react';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import React, { useState } from 'react';
import { deleteApi } from '../../../../apiUtils';
import { useTranslation } from '../../../../contexts/translation';
import { confirmDialogActions, useConfirmDialog } from '../../../../hooks/useConfirmDialog';
import { PageContentWrapper } from '../../../../layouts/contentWrapper/PageContentWrapper';
import { Task, TaskComment } from '../../../../types/task.types';
import { API_URL } from '../../../../utils/apiUrl';
import { displayDateTimeFormat_CZ, displayDateTimeFormat_EN } from '../../../../utils/dates.types';
import { ITab } from '../../../Assets/AssetsEdit';
import { TaskServiceCommentsForm } from './TaskServiceCommentsForm';
import { TaskServiceCommentsUpdateForm } from './TaskServiceCommentsUpdateForm';

interface Props extends ITab {
  task: Task;
  comments: any;
  enableEdit?: boolean;
  enableDelete?: boolean;
  onAdd: (comment: any) => void;
  onUpdate?: (comment: any) => void;
  onDelete?: (commentId: number) => void;
}

export const TaskServiceComments = ({
  task,
  comments,
  onAdd,
  enableEdit,
  enableDelete,
  onDelete,
  onUpdate,
}: Props) => {
  const { translate, lang } = useTranslation();
  const transFormName = 'Task.ServiceDeskComment';
  const dateTimeFormat = lang === 'en-US' ? displayDateTimeFormat_EN : displayDateTimeFormat_CZ;

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateFormId, setShowUpdateFormId] = useState<number | null>(null);

  const { open } = useConfirmDialog({
    title: translate!('Delete', ''),
    bodyHtml: translate!('Are you sure?', ''),
    cancelButtonTitle: translate!('Cancel', ''),
    confirmButtonTitle: translate!('Confirm', ''),
  });

  const handleDelete = async (commentId: number) => {
    open().then(async (c) => {
      if (c === confirmDialogActions.CONFIRM) {
        await deleteApi({
          url: `${API_URL.TASK_COMMENT_DELETE}`,
          id: commentId,
          callAfterSuccess: () => {
            if (onDelete) onDelete(commentId);
            notify(translate!('Comment deleted', transFormName), 'success');
          },
        });
      } else {
        return false;
      }
    });
  };

  return (
    <PageContentWrapper noContentBlock loading={false} noDxCard>
      <Button
        type="default"
        stylingMode="contained"
        onClick={() => setShowCreateForm(!showCreateForm)}
        text={translate!(
          showCreateForm ? 'Cancel adding comment' : 'Add new comment',
          transFormName,
        )}
      />
      <TaskServiceCommentsForm
        taskId={task.id}
        visible={showCreateForm}
        onSuccess={(comment: any) => {
          setShowCreateForm(false);
          onAdd(comment);
        }}
      />
      <div className="comments-list">
        {comments.length > 0 ? (
          comments.map((comment: TaskComment) => {
            return (
              <div key={comment.id} className="comment">
                <div className="header">
                  <div className="date">{moment(comment.created).format(dateTimeFormat)}</div>
                  <div className="user">{comment.createdByName}</div>
                  <div className="buttons">
                    {enableEdit && !showUpdateFormId && (
                      <Button
                        icon={'edit'}
                        type={'default'}
                        stylingMode={'text'}
                        onClick={() => setShowUpdateFormId(comment.id)}
                      />
                    )}
                    {enableDelete && !showUpdateFormId && (
                      <Button
                        icon={'remove'}
                        type={'default'}
                        stylingMode={'text'}
                        onClick={() => handleDelete(comment.id)}
                      />
                    )}
                  </div>
                </div>
                {showUpdateFormId && showUpdateFormId === comment.id ? (
                  <TaskServiceCommentsUpdateForm
                    taskId={task.id}
                    commentId={showUpdateFormId}
                    defaultComment={comment.comment}
                    visible={!!showUpdateFormId}
                    onSuccess={(comment: any) => {
                      if (onUpdate) onUpdate(comment);
                      setShowUpdateFormId(null);
                    }}
                    onCancel={() => setShowUpdateFormId(null)}
                  />
                ) : (
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: comment.comment,
                    }}
                  />
                )}
              </div>
            );
          })
        ) : (
          <div>{translate!('No comments', transFormName)}</div>
        )}
      </div>
    </PageContentWrapper>
  );
};
